import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import { materialId } from './wms-master.seed'
import { RM_FINDING_REASONS, rmFindingReasonId } from './wms-setup.seed'

/**
 * Inventory & Laporan C16 (INV-01 Stok Inventory, INV-02 Riwayat Mutasi Stok, INV-03 Dashboard Operasional).
 * Tiga halaman ini hanya membaca stok yang sudah ada, kecuali tab "Barang Not Good" di INV-01 yang memakai
 * `rm_ng_item`. Seed menambahkan:
 * - `tx_log` riwayat contoh prototype v7.44 (rantai RM A/B, picking, FG putaway) — `tx_log` sebelumnya kosong.
 * - `stock_movement` `RM_PICK` untuk lima pick-commit hari ini (sumber kolom "Keluar" Kartu Stok Bulanan).
 * - `rm_ng_item` antrean Barang Not Good (opname, temuan harian, menunggu approval) — seed prototype `rm.ng_zone` / `rm.ng_temuan`.
 *
 * Kolom di luar ERD v010 ([ERD-GAP], laporan 17): `rm_ng_item.{finding_no, qr_code, batch_no, note, reported_by, approved_by,
 * approved_at, source_ref, log, unit_name}`; `rm_ng_item.item_status` bertambah `PENDING_APPROVAL` / `REJECTED`.
 */

const utc = (at: string) => new Date(`${at.replace(' ', 'T')}:00+07:00`).toISOString()
const audit = (userKey: keyof typeof USER_IDS | null, at: string) => ({
  status: 'published',
  sort: null,
  user_created: userKey ? USER_IDS[userKey] : null,
  date_created: utc(at),
  user_updated: null,
  date_updated: null,
  user_deleted: null,
  date_deleted: null
})

const WH_RM = seedId(SEED_GROUP.warehouse, 1)
const WH_FG = seedId(SEED_GROUP.warehouse, 2)

type LogSpec = [
  at: string,
  type: string,
  page: string,
  action: string,
  ref: string,
  detail: string,
  wh: 'RM' | 'FG',
  user: string
]

/** Riwayat contoh prototype (`tx.log` seed) — diurutkan lama → baru, id deterministik per urutan. */
const TX: LogSpec[] = [
  [
    '2026-08-01 06:40',
    'RM Receiving',
    'PRC-05',
    'kedatangan-baru',
    'IN-260801-001',
    'Kedatangan IN-260801-001 (DO SJ-SUP-000155) dari PT Bukit Muria Jaya — 1 baris material, 1 batch tercatat, PO: PO-2607-088',
    'RM',
    'yulia'
  ],
  [
    '2026-08-01 06:55',
    'RM Receiving',
    'IRM-09',
    'print-pallet',
    'PLT-000216',
    'QR pallet PLT-000216 dicetak untuk menampung batch ETK-BOLD20-260801-01',
    'RM',
    'yulia'
  ],
  [
    '2026-08-01 07:05',
    'RM Receiving',
    'IRM-10',
    'palletizing-finalize',
    'PLT-000216',
    'Pallet PLT-000216 (1 material, 10 Box total) selesai — masuk antrian QC: QRT-260801-090',
    'RM',
    'yulia'
  ],
  [
    '2026-08-01 07:15',
    'RM QC',
    'IRM-04',
    'qc-dijadwalkan',
    'QRT-260801-090',
    'ETK-BOLD20 · ETK-BOLD20-260801-01 — 10 Box ditugaskan ke Tono Wijaya pada 2026-08-01 08:00 (60 mnt)',
    'RM',
    'galih'
  ],
  [
    '2026-08-01 08:04',
    'RM QC',
    'IRM-05',
    'qc-pallet-scan',
    'PLT-000216',
    'Pallet PLT-000216 dipindai — 1 material siap diperiksa',
    'RM',
    'tono'
  ],
  [
    '2026-08-01 08:55',
    'RM QC',
    'IRM-05',
    'qc-save',
    'PLT-000216',
    'Pallet PLT-000216 — 1 material di-QC: 10 Good / 0 NG, tugas penempatan PWR-260801-001 dibuat',
    'RM',
    'tono'
  ],
  [
    '2026-08-01 09:10',
    'RM Putaway',
    'IRM-07',
    'putaway',
    'ETK-BOLD20:20260801:01:0001',
    'ETK-BOLD20 10 Box → R01-B01-L01-P01',
    'RM',
    'karno'
  ],
  [
    '2026-08-01 09:12',
    'RM Putaway',
    'IRM-07',
    'pallet-released',
    'PLT-000216',
    'Seluruh isi pallet PLT-000216 tersimpan — pallet kembali ke kolom kosong',
    'RM',
    'karno'
  ],
  [
    '2026-08-05 10:22',
    'RM Putaway',
    'IRM-07',
    'putaway',
    'FLT-B06:20260805:01:0001',
    'FLT-B06 15 Dos → R01-B02-L01-P01',
    'RM',
    'karno'
  ],
  [
    '2026-08-10 08:40',
    'RM Putaway',
    'IRM-07',
    'putaway',
    'PPR-REG:20260810:01:0001',
    'PPR-REG 20 Roll → R02-B01-L01-P01',
    'RM',
    'karno'
  ],
  [
    '2026-08-12 07:10',
    'RM Receiving',
    'PRC-05',
    'kedatangan-baru',
    'IN-260812-001',
    'Kedatangan IN-260812-001 (DO SJ-SUP-000171) dari PT Indra Jaya — 1 baris material, 1 batch tercatat (10 Box), PO: PO-2608-091',
    'RM',
    'yulia'
  ],
  [
    '2026-08-12 07:45',
    'RM Receiving',
    'IRM-10',
    'palletizing-finalize',
    'PLT-000217',
    'Pallet PLT-000217 (1 material, 10 Box total) selesai — masuk antrian QC: QRT-260812-091',
    'RM',
    'yulia'
  ],
  [
    '2026-08-12 08:00',
    'RM QC',
    'IRM-04',
    'qc-dijadwalkan',
    'QRT-260812-091',
    'ETK-XTRA20 · ETK-XTRA20-260812-01 — 10 Box ditugaskan ke Karno pada 2026-08-12 09:00 (90 mnt)',
    'RM',
    'galih'
  ],
  [
    '2026-08-12 10:00',
    'RM QC',
    'IRM-05',
    'qc-save',
    'PLT-000217',
    'Pallet PLT-000217 — 1 material di-QC: 8 Good / 2 NG (Cut to Cut) — label Bagus dan label NG terbit',
    'RM',
    'karno'
  ],
  [
    '2026-08-12 11:05',
    'RM Putaway',
    'IRM-07',
    'putaway',
    'ETK-XTRA20:20260812:01:0001',
    'ETK-XTRA20 8 Box → R03-B01-L01-P01',
    'RM',
    'karno'
  ],
  [
    '2026-07-16 09:30',
    'Inventory Transfer',
    'TRF-03',
    'transfer-diterima',
    'ETK-BOLD20:20260715:01:0001',
    'ETK-BOLD20 4 Box WH-RM → WH-SGI',
    'RM',
    'agus'
  ],
  [
    '2026-08-16 10:30',
    'RM NG Placement',
    'IRM-07',
    'ng-placement',
    'ETK-XTRA20:20260812:01:0002',
    'ETK-XTRA20 2 Box → QN-NG (Karantina NG) — tiket NGT-260816-090',
    'RM',
    'karno'
  ],
  [
    '2026-08-18 07:30',
    'RM Request',
    'REQ-01',
    'request-dibuat',
    'REQ-260818-001',
    'Request material SPK 15-10/JLC/20 — Tabaco Bold 20, 2.080.000 batang',
    'RM',
    'joko'
  ],
  [
    '2026-08-18 08:05',
    'RM Request',
    'REQ-02A',
    'tugas-ditugaskan',
    'PICK-260818-001',
    'Tugas pengambilan PICK-260818-001 ditugaskan ke Karno — 1 unit, ETK-BOLD20 dari R01-B01-L01-P01',
    'RM',
    'yulia'
  ],
  [
    '2026-08-18 08:15',
    'RM Picking',
    'REQ-03',
    'pick-commit',
    'FLT-B06:20260805:01:0001',
    'FLT-B06 — 5 Dos → SPK 39-08/JLC/26',
    'RM',
    'tono'
  ],
  [
    '2026-08-18 08:40',
    'RM Picking',
    'REQ-03',
    'pick-commit',
    'PPR-REG:20260810:01:0001',
    'PPR-REG — 6 Roll → SPK 15-10/JLC/20',
    'RM',
    'karno'
  ],
  [
    '2026-08-18 08:40',
    'Return Order',
    'RTN-01',
    'ro-create',
    'RO-260818-002',
    'RO RO-260818-002 (PT Indra Jaya) dibuat dari 1 tiket NG — supplier ditelusuri dari batch kedatangan ETK-XTRA20-260812-01',
    'RM',
    'yulia'
  ],
  [
    '2026-08-18 09:05',
    'RM Picking',
    'REQ-03',
    'pick-commit',
    'ETK-BOLD20:20260801:01:0001',
    'ETK-BOLD20 — 3 Box → SPK 22-08/JLC/26',
    'RM',
    'tono'
  ],
  [
    '2026-08-18 09:30',
    'RM Picking',
    'REQ-03',
    'pick-commit',
    'ETK-XTRA20:20260812:01:0001',
    'ETK-XTRA20 — 2 Box → SPK 30-08/JLC/26',
    'RM',
    'karno'
  ],
  [
    '2026-08-18 09:52',
    'RM Picking',
    'REQ-03',
    'pick-commit',
    'FLT-B06:20260805:01:0001',
    'FLT-B06 — 4 Dos (sebagian, sisa 6 Dos tetap di R01-B02-L01-P01) → SPK 22-08/JLC/26',
    'RM',
    'tono'
  ],
  [
    '2026-08-10 06:50',
    'FG Auto Labeler',
    'IFG-01',
    'alf-feed',
    'TO12-20260810-0103-B001',
    'Data mesin MOLLINS-1 shift 1 masuk — Tabaco One 12, 1 Karton + 1 Bal',
    'FG',
    'ulil'
  ],
  [
    '2026-08-10 07:25',
    'FG Terima & Cek',
    'IFG-01',
    'fg-receive',
    'TO12-20260810-0103-B001',
    'Hitungan fisik cocok dengan data mesin — label diterbitkan, tugas penyimpanan dibuat',
    'FG',
    'ulil'
  ],
  [
    '2026-08-10 09:05',
    'FG Putaway',
    'IFG-04',
    'fg-putaway',
    'LBL-000301',
    'Tabaco One 12 1 Karton → pallet A1-P4',
    'FG',
    'budi'
  ],
  [
    '2026-08-10 09:12',
    'FG Putaway',
    'IFG-04',
    'fg-putaway',
    'LBL-000302',
    'Tabaco One 12 1 Bal → pallet D1-P3',
    'FG',
    'budi'
  ],
  [
    '2026-08-15 06:45',
    'FG Auto Labeler',
    'IFG-01',
    'alf-feed',
    'TB20-20260815-0109-B001',
    'Data mesin MOLLINS-1 shift 1 masuk — Tabaco Bold 20, 1 Karton (4 Bal) + 2 Slop lepas',
    'FG',
    'ulil'
  ],
  [
    '2026-08-15 07:30',
    'FG Terima & Cek',
    'IFG-01',
    'fg-receive',
    'TB20-20260815-0109-B001',
    'Hitungan fisik cocok — 1 label Karton, 4 label Bal turunan, dan 2 label Slop diterbitkan',
    'FG',
    'ulil'
  ],
  [
    '2026-08-15 07:34',
    'FG Penerbitan Label',
    'IFG-01',
    'fg-label',
    'LBL-000303',
    'Label Karton terbit beserta empat Bal turunannya: LBL-000341, LBL-000342, LBL-000343, LBL-000344',
    'FG',
    'ulil'
  ],
  [
    '2026-08-15 08:40',
    'FG Putaway',
    'IFG-04',
    'fg-putaway',
    'LBL-000303',
    'Tabaco Bold 20 1 Karton (berisi 4 Bal) → pallet A1-P1',
    'FG',
    'sari'
  ],
  [
    '2026-08-15 08:52',
    'FG Putaway',
    'IFG-04',
    'fg-putaway',
    'LBL-000304',
    'Tabaco Bold 20 1 Slop → Container A',
    'FG',
    'sari'
  ],
  [
    '2026-08-15 08:55',
    'FG Putaway',
    'IFG-04',
    'fg-putaway',
    'LBL-000305',
    'Tabaco Bold 20 1 Slop → Container A',
    'FG',
    'sari'
  ],
  [
    '2026-08-18 09:35',
    'Stock Opname',
    'OPN-03',
    'opn-submit',
    'LBL-000303',
    'Pallet A1-P1: label terpindai dan sesuai catatan sistem',
    'FG',
    'sari'
  ]
]

/** Lima pick-commit (unit sumber → qty besar) yang tercatat di `stock_movement` untuk kolom "Keluar" Kartu Stok. */
const PICKS: [unitNo: number, qtyBig: number, at: string, docRef: string][] = [
  [3, 5, '2026-08-18 08:15', 'PICK-260818-002'],
  [4, 6, '2026-08-18 08:40', 'PICK-260818-003'],
  [2, 3, '2026-08-18 09:05', 'PICK-260818-004'],
  [5, 2, '2026-08-18 09:30', 'PICK-260818-005'],
  [3, 4, '2026-08-18 09:52', 'PICK-260818-006']
]

export function applyInventoryData(seed: SeedTables): SeedTables {
  const units = seed.rm_unit ?? []
  const unitOf = (qr: string) => units.find(unit => String(unit.qr_code).startsWith(qr))
  const unitByNo = (no: number) => units.find(unit => unit.id === seedId(SEED_GROUP.rmUnit, no))

  const tx_log: LocalRow[] = TX.slice()
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([at, type, page, action, ref, detail, wh, user], index) => ({
      ...audit(null, at),
      id: seedId(SEED_GROUP.txLog, index + 1),
      ts: utc(at),
      log_type: type,
      page_code: page,
      action,
      doc_ref: ref,
      detail,
      warehouse_id: wh === 'RM' ? WH_RM : WH_FG,
      user_id: USER_IDS[user]
    }))

  const stock_movement: LocalRow[] = PICKS.map(([unitNo, qtyBig, at, docRef], index) => {
    const unit = unitByNo(unitNo) as LocalRow
    return {
      ...audit('karno', at),
      id: seedId(SEED_GROUP.stockMovement, index + 1),
      ts: utc(at),
      movement_type: 'RM_PICK',
      unit_id: unit.id,
      qty_small: qtyBig * Number(unit.small_per_big_snapshot ?? 1),
      from_rack_bin_id: unit.rack_bin_id ?? null,
      to_rack_bin_id: null,
      from_warehouse_zone_id: null,
      to_warehouse_zone_id: null,
      doc_ref: docRef
    }
  })

  const ng = (n: number) => seedId(SEED_GROUP.rmNgItem, n)
  const reasons = (code: string) => [rmFindingReasonId(code)]
  const mldUnit = unitOf('TSG-MLD:20260818:01') as LocalRow
  const fltUnit = unitByNo(3) as LocalRow
  const pprUnit = unitByNo(4) as LocalRow
  const innUnit = unitOf('INN-BLK96:20260801') as LocalRow
  const regUnitA = unitOf('TSG-REG:20260814:01') as LocalRow
  const regUnitB = unitOf('TSG-REG:20260816:01') as LocalRow
  const base = {
    sticker_ngq: null,
    destruction_id: null,
    move_operator_id: null,
    move_task_no: null,
    approved_by: null,
    approved_at: null
  }
  const rm_ng_item: LocalRow[] = [
    {
      ...audit('galih', '2026-08-18 10:20'),
      ...base,
      id: ng(1),
      ngz_no: 'NGZ-260818-001',
      finding_no: null,
      source: 'OPNAME',
      source_ref: 'OPN-RM-260715-001',
      unit_id: mldUnit.id,
      material_id: materialId('TSG-MLD'),
      qty: 2,
      unit_name: 'Sak',
      reason_id: reasons('LEMBAB')[0],
      reason_ids: reasons('LEMBAB'),
      item_status: 'AWAITING_LABEL',
      rack_bin_id: mldUnit.rack_bin_id,
      warehouse_zone_id: mldUnit.warehouse_zone_id,
      qr_code: mldUnit.qr_code,
      batch_no: 'TSG-MLD-260818-01',
      note: null,
      reported_by: USER_IDS.karno,
      log: [
        {
          at: '2026-08-18 10:20',
          ev: 'Terbit dari opname OPN-RM-260715-001 — 2 Sak Lembab / Berjamur (pecahan dari stiker TSG-MLD:20260818:01:0001)',
          by: USER_IDS.galih
        }
      ]
    },
    {
      ...audit('tono', '2026-08-18 10:22'),
      ...base,
      id: ng(2),
      ngz_no: 'NGZ-260818-002',
      finding_no: null,
      source: 'OPNAME',
      source_ref: 'OPN-RM-260715-001',
      unit_id: fltUnit.id,
      material_id: materialId('FLT-B06'),
      qty: 1,
      unit_name: 'Dos',
      reason_id: reasons('SOBEK')[0],
      reason_ids: reasons('SOBEK'),
      sticker_ngq: 'NGQ-260818-001',
      item_status: 'MOVED',
      rack_bin_id: null,
      warehouse_zone_id: null,
      qr_code: fltUnit.qr_code,
      batch_no: 'FLT-B06-260805-01',
      note: null,
      reported_by: USER_IDS.tono,
      move_operator_id: USER_IDS.karno,
      move_task_no: 'TNG-260818-001',
      log: [
        {
          at: '2026-08-18 11:05',
          ev: 'Stiker Not Good NGQ-260818-001 dicetak — barang masuk antrean pemindahan',
          by: USER_IDS.yulia
        },
        { at: '2026-08-18 13:10', ev: 'Dipindah 1 Dos ke ZNG-RM (QR area discan)', by: USER_IDS.karno }
      ]
    },
    {
      ...audit('karno', '2026-08-15 14:05'),
      ...base,
      id: ng(3),
      ngz_no: 'NGZ-260815-003',
      finding_no: 'TMN-260815-001',
      source: 'TEMUAN',
      source_ref: 'TMN-260815-001',
      unit_id: pprUnit.id,
      material_id: materialId('PPR-REG'),
      qty: 2,
      unit_name: 'Roll',
      reason_id: reasons('KOTOR')[0],
      reason_ids: reasons('KOTOR'),
      sticker_ngq: 'NGQ-260815-002',
      item_status: 'MOVED',
      rack_bin_id: null,
      warehouse_zone_id: null,
      qr_code: pprUnit.qr_code,
      batch_no: 'PPR-REG-260801-01',
      note: 'Kena rembesan atap saat hujan besar.',
      reported_by: USER_IDS.karno,
      approved_by: USER_IDS.galih,
      approved_at: utc('2026-08-15 14:05'),
      move_operator_id: USER_IDS.tono,
      move_task_no: 'TNG-260815-001',
      log: [
        { at: '2026-08-15 13:40', ev: 'Dilaporkan Karno — 2 Roll Terkontaminasi / Kotor', by: USER_IDS.karno },
        { at: '2026-08-15 14:05', ev: 'Disetujui Supervisor WH RM', by: USER_IDS.galih },
        {
          at: '2026-08-15 14:40',
          ev: 'Stiker Not Good NGQ-260815-002 dicetak — barang masuk antrean pemindahan',
          by: USER_IDS.yulia
        },
        { at: '2026-08-15 15:30', ev: 'Dipindah 2 Roll ke ZNG-RM (QR area discan)', by: USER_IDS.tono }
      ]
    },
    {
      ...audit('tono', '2026-08-18 09:15'),
      ...base,
      id: ng(4),
      ngz_no: null,
      finding_no: 'TMN-260818-002',
      source: 'TEMUAN',
      source_ref: 'TMN-260818-002',
      unit_id: innUnit.id,
      material_id: materialId('INN-BLK96'),
      qty: 1,
      unit_name: 'Bundle',
      reason_id: reasons('SOBEK')[0],
      reason_ids: reasons('SOBEK'),
      item_status: 'PENDING_APPROVAL',
      rack_bin_id: innUnit.rack_bin_id ?? null,
      warehouse_zone_id: null,
      qr_code: innUnit.qr_code,
      batch_no: 'INN-BLK96-260801-01',
      note: 'Bundle paling bawah tertimpa, kemasan robek.',
      reported_by: USER_IDS.tono,
      log: [
        { at: '2026-08-18 09:15', ev: 'Dilaporkan Tono Wijaya — 1 Bundle Kemasan Sobek', by: USER_IDS.tono },
        { at: '2026-08-18 09:15', ev: 'Dikirim ke Supervisor WH RM untuk persetujuan', by: null }
      ]
    },
    ...[
      [5, 'NGZ-260818-004', 'NGQ-260818-003', regUnitA, 6, 'TSG-REG-260814-01', '2026-08-18 10:25'],
      [6, 'NGZ-260818-005', 'NGQ-260818-004', regUnitB, 4, 'TSG-REG-260816-01', '2026-08-18 10:26']
    ].map(([n, ngz, sticker, unit, qty, batch, at]) => ({
      ...audit('galih', at as string),
      ...base,
      id: ng(n as number),
      ngz_no: ngz,
      finding_no: null,
      source: 'OPNAME',
      source_ref: 'OPN-RM-260715-001',
      unit_id: (unit as LocalRow).id,
      material_id: materialId('TSG-REG'),
      qty,
      unit_name: 'Sak',
      reason_id: reasons('LEMBAB')[0],
      reason_ids: reasons('LEMBAB'),
      sticker_ngq: sticker,
      item_status: 'MOVING',
      rack_bin_id: (unit as LocalRow).rack_bin_id,
      warehouse_zone_id: (unit as LocalRow).warehouse_zone_id,
      qr_code: (unit as LocalRow).qr_code,
      batch_no: batch,
      note: null,
      reported_by: USER_IDS.karno,
      move_operator_id: USER_IDS.karno,
      move_task_no: 'TNG-260818-002',
      log: [
        {
          at: at as string,
          ev: `Terbit dari opname OPN-RM-260715-001 — ${qty} Sak Lembab / Berjamur`,
          by: USER_IDS.galih
        },
        {
          at: '2026-08-18 11:15',
          ev: `Stiker Not Good ${sticker} dicetak — barang masuk antrean pemindahan`,
          by: USER_IDS.yulia
        },
        { at: '2026-08-18 11:16', ev: 'Ditugaskan ke Karno (TNG-260818-002) — pindah ke ZNG-RM', by: USER_IDS.yulia }
      ]
    }))
  ]
  void RM_FINDING_REASONS

  return {
    ...seed,
    tx_log: [...(seed.tx_log ?? []), ...tx_log],
    stock_movement: [...(seed.stock_movement ?? []), ...stock_movement],
    rm_ng_item: [...(seed.rm_ng_item ?? []), ...rm_ng_item]
  }
}
